import { Card, Flex, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DdqProdOpsWorkflowStatus, DdqProdOpsWorkflowType } from "~/graphql/supergraphEnums";
import { STATUS_LABELS, WORKFLOW_TYPE_LABELS } from "../labels";
import * as styles from "./styles_vanilla.css";
import { useDdqQueueSummaryCounts } from "./useDdqQueueSummaryCounts";
import type { DdqQueueSummaryCounts } from "./useDdqQueueSummaryCounts";

export type SummaryFilterCardsProps = {
  cardWorkflowTypes: Set<DdqProdOpsWorkflowType>;
  onToggleWorkflowType: (type: DdqProdOpsWorkflowType) => void;
  cardStatuses: Set<DdqProdOpsWorkflowStatus>;
  onToggleStatus: (status: DdqProdOpsWorkflowStatus) => void;
};

type FilterCardDef =
  | { kind: "workflowType"; value: DdqProdOpsWorkflowType }
  | { kind: "status"; value: DdqProdOpsWorkflowStatus };

// `In progress`/`In review` have no card of their own -- the Status column's set filter is
// the only way to isolate those (see Grid/columnDefs.ts).
const FILTER_CARDS: FilterCardDef[] = [
  { kind: "workflowType", value: DdqProdOpsWorkflowType.DdqRequest },
  { kind: "workflowType", value: DdqProdOpsWorkflowType.OriginalFormat },
  { kind: "workflowType", value: DdqProdOpsWorkflowType.BackfillDdq },
  { kind: "status", value: DdqProdOpsWorkflowStatus.Pending },
  { kind: "status", value: DdqProdOpsWorkflowStatus.OnHold },
];

const cardLabel = (card: FilterCardDef): string =>
  card.kind === "workflowType" ? WORKFLOW_TYPE_LABELS[card.value] : STATUS_LABELS[card.value];

const cardCount = (card: FilterCardDef, counts: DdqQueueSummaryCounts): number =>
  (card.kind === "workflowType"
    ? counts.workflowType[card.value]
    : counts.status[card.value]) ?? 0;

const isCardSelected = (
  card: FilterCardDef,
  cardWorkflowTypes: Set<DdqProdOpsWorkflowType>,
  cardStatuses: Set<DdqProdOpsWorkflowStatus>,
): boolean =>
  card.kind === "workflowType"
    ? cardWorkflowTypes.has(card.value)
    : cardStatuses.has(card.value);

export const SummaryFilterCards = ({
  cardWorkflowTypes,
  onToggleWorkflowType,
  cardStatuses,
  onToggleStatus,
}: SummaryFilterCardsProps) => {
  const { counts, isDueBucketTruncated, isLoading, isUnavailable } = useDdqQueueSummaryCounts();
  const showPlaceholder = isLoading || isUnavailable;

  return (
    <Flex className={styles.row} gap="small" data-testid="ddq-summary-filter-cards">
      {FILTER_CARDS.map((card) => {
        const selected = isCardSelected(card, cardWorkflowTypes, cardStatuses);
        return (
          <Card
            key={`${card.kind}-${card.value}`}
            isInteractive
            padding="sm"
            className={selected ? styles.cardSelected : styles.card}
            onClick={() =>
              card.kind === "workflowType"
                ? onToggleWorkflowType(card.value)
                : onToggleStatus(card.value)
            }
            aria-pressed={selected}
            data-testid={`ddq-summary-card-${card.value}`}
          >
            <Flex flexDirection="column">
              <Text variant="regular-xl">
                {showPlaceholder ? "-" : cardCount(card, counts)}
              </Text>
              <Text variant="regular-sm" color={Colors.Neutral700}>
                {cardLabel(card)}
              </Text>
            </Flex>
          </Card>
        );
      })}
      <Card padding="sm" className={styles.infoCard} data-testid="ddq-summary-card-due-today">
        <Flex flexDirection="column">
          <Text variant="regular-xl">
            {showPlaceholder ? "-" : `${counts.dueToday}${isDueBucketTruncated ? "+" : ""}`}
          </Text>
          <Text variant="regular-sm" color={Colors.Neutral700}>
            Due today
          </Text>
        </Flex>
      </Card>
      <Card padding="sm" className={styles.infoCard} data-testid="ddq-summary-card-overdue">
        <Flex flexDirection="column">
          <Text variant="regular-xl">
            {showPlaceholder ? "-" : `${counts.overdue}${isDueBucketTruncated ? "+" : ""}`}
          </Text>
          <Text variant="regular-sm" color={Colors.Neutral700}>
            Overdue
          </Text>
        </Flex>
      </Card>
    </Flex>
  );
};
